import React, { Fragment, useState, useRef, useEffect } from "react";
import HoverIconButton from "components/HoverIconButton";
import { FieldArray, useFormikContext } from "formik";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import { getPrimaryScaffold } from "utils/shortLink";
import CheckoutProductPricePicker from "components/CheckoutProductPricePicker";
import classnames from "classnames";
import SlimCTA from "components/SlimCTA";
import { STATE_KEYS } from "utils/constants/state";
import InheritenceDisplay from "components/InheritenceDisplay";
import PrimariesDisplayList from "components/PrimariesDisplayList";

const NO_PRODUCTS_SUBHEADER = "Add products to checkout.";

const CheckoutProductPricesPickers = ({
  rootPath,
  name,
  editMode,
  inherited
}) => {
  const { values } = useFormikContext();
  const fieldName = getFieldPath(rootPath, name);

  const autoTaxes = Boolean(
    get(values, STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED)
  );

  /**
   * Set model key in state which can be referenced for showing a particular model on mount
   * - UseCase:
   * - when a User clicks the add product CTA
   * - we add a scaffold primary to the primaries field as a placeholder, which we will fill with a selection
   * - rather than require the user to click again to show the product menu we set a key for the recently scaffolded primary
   * - then we use this key as prop to control showing the product selector menu on mount
   */
  const [showOnMountKey, setShowOnMountKey] = useState();
  const firstNameRef = useRef(null);
  useEffect(() => {
    firstNameRef.current && firstNameRef.current.focus();
  }, []);

  const models = get(values, fieldName);
  const hasModels = models && models.length > 0;
  const inheritedPrimaries =
    get(inherited, STATE_KEYS.SHORT_LINK.PRIMARIES) || [];
  const hasInheritedPrimaries = inheritedPrimaries.length > 0;

  return (
    <Fragment>
      <FieldArray
        name={fieldName}
        render={(arrayHelpers) => {
          const addPrimary = () => {
            const primary = getPrimaryScaffold();
            setShowOnMountKey(primary.key);
            arrayHelpers.push(primary);
          };
          const noModelsCTAProps = editMode
            ? {
                customClasses: {
                  container:
                    "w-100 flex flex-column flex-row-ns justify-between items-start items-center-ns bg-white",
                  subheader: "f6 lh-copy"
                },
                subheader: NO_PRODUCTS_SUBHEADER,
                actions: [
                  {
                    copy: "Add",
                    onClick: addPrimary
                  }
                ]
              }
            : {
                subheader: "This link has no products."
              };

          return hasModels || hasInheritedPrimaries ? (
            <ul className="list ma0 pa0 w-100 h-100">
              {hasInheritedPrimaries ? (
                <InheritenceDisplay hideAction>
                  <div className="pb2">
                    <PrimariesDisplayList
                      primaries={inheritedPrimaries}
                      autoTaxes={autoTaxes}
                    />
                  </div>
                </InheritenceDisplay>
              ) : null}
              {models.map((model, modelIx) => {
                const modelName = `${fieldName}[${modelIx}]`;

                const productPickerProps = {
                  name: modelName,
                  modelIx: modelIx,
                  arrayHelpers: arrayHelpers,
                  editMode,
                  showOnMount: showOnMountKey && showOnMountKey === model.key,
                  unsetMountKey: () => setShowOnMountKey(null)
                };
                if (!modelIx) {
                  productPickerProps.innerNameRef = firstNameRef;
                }

                /**
                 * In create mode we append a placeholder key to the new models
                 * In edit mode we use the chosen price
                 */
                const keyName = model.key || model.price;
                return (
                  <div
                    key={`${keyName}-${modelIx}`}
                    className={classnames("w-100", {
                      pt3: modelIx || hasInheritedPrimaries
                    })}
                  >
                    <CheckoutProductPricePicker {...productPickerProps} />
                  </div>
                );
              })}
              {editMode ? (
                <div className="flex flex-row justify-start pt3">
                  <div className="flex flex-row w-100 justify-end items-center">
                    <div className="fw5 f6 pr2">{`Add product`}</div>
                    <HoverIconButton
                      type="add"
                      tooltip="Add"
                      onClick={addPrimary}
                    />
                  </div>
                </div>
              ) : null}
            </ul>
          ) : (
            <SlimCTA key="no-models-cta" {...noModelsCTAProps} />
          );
        }}
      />
    </Fragment>
  );
};

export default CheckoutProductPricesPickers;
